import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  type ChartData,
  type ChartOptions,
  type Plugin,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { Skeleton } from "@libertify/ui";
import { analyticsTheme } from "./analyticsTheme";
import { AnalyticsCard } from "./AnalyticsCard";
import {
  getSharedPlugins,
  getPercentageAxis,
  getCountAxis,
  getCategoryAxis,
  getTimestampAxis,
} from "./utils/";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  annotationPlugin
);

// Draws each bar's value just outside its end.
const valueLabelsPlugin: Plugin<"bar"> = {
  id: "barValueLabels",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const horizontal = chart.options.indexAxis === "y";
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;
      meta.data.forEach((element, index) => {
        const value = dataset.data[index];
        if (value == null) return;
        ctx.save();
        ctx.fillStyle = analyticsTheme.textBody;
        ctx.font = "600 12px sans-serif";
        if (horizontal) {
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillText(String(value), element.x + 6, element.y);
        } else {
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillText(String(value), element.x, element.y - 6);
        }
        ctx.restore();
      });
    });
  },
};

export interface BarDatasetConfig {
  label: string;
  /** A null value draws no bar and no value label (unknown, as opposed to 0). */
  data: (number | null)[];
  color: string;
}

export interface AnalyticsBarChartProps {
  title: string;
  subtitle?: string;
  /** How to read the chart, shown behind an info icon next to the title. */
  info?: string;
  /** Shown next to the title, e.g. a tag saying what the chart counts. */
  badge?: React.ReactNode;
  /** An array label is drawn on several lines. */
  labels: (string | string[])[];
  datasets: BarDatasetConfig[];
  loading?: boolean;
  height?: number;
  /**
   * Fixed plot height, legend and axis included. The card then grows around
   * it (the header takes the lines it needs) instead of taking its
   * height from its container, and still stretches to a taller row.
   */
  plotHeight?: number;
  direction?: "horizontal" | "vertical";
  /** Value-axis scale. "percentage" (default, 0–100 %) or "count" (raw integers). */
  valueAxis?: "percentage" | "count";
  /** Draw each bar's value next to it (no hover required). */
  showValueLabels?: boolean;
  /**
   * Category-axis scale. "category" (default) spaces bars by index; "linear"
   * positions them by the numeric `xValues` (e.g. a seconds timeline).
   */
  xScaleType?: "category" | "linear";
  /** Numeric x-coordinate for each bar, required when `xScaleType` is "linear". */
  xValues?: number[];
  /** Upper bound of a "linear" category axis (e.g. total video length). */
  xMax?: number;
  chartOptions?: Partial<ChartOptions<"bar">>;
  /** Share of the category a bar group takes (default 0.7). */
  categoryPercentage?: number;
  /** Share of the group each bar takes (default 0.6). */
  barPercentage?: number;
}

export const AnalyticsBarChart: React.FC<AnalyticsBarChartProps> = ({
  title,
  subtitle,
  info,
  badge,
  labels,
  datasets,
  loading = false,
  height = 200,
  plotHeight,
  direction = "horizontal",
  valueAxis = "percentage",
  showValueLabels = false,
  xScaleType = "category",
  xValues,
  xMax,
  chartOptions,
  categoryPercentage = 0.7,
  barPercentage = 0.6,
}) => {
  const defaultOptions = useMemo<ChartOptions<"bar">>(() => {
    const indexAxis = direction === "horizontal" ? "y" : "x";
    const valueAxisKey = direction === "horizontal" ? "x" : "y";
    const categoryAxisKey = direction === "horizontal" ? "y" : "x";

    return {
      indexAxis,
      responsive: true,
      maintainAspectRatio: false,
      // Value labels are drawn past the end of each bar: leave them room.
      layout: showValueLabels
        ? { padding: direction === "horizontal" ? { right: 56 } : { top: 24 } }
        : undefined,
      plugins: getSharedPlugins(),
      scales: {
        [valueAxisKey]:
          valueAxis === "count" ? getCountAxis() : getPercentageAxis(),
        [categoryAxisKey]:
          xScaleType === "linear" ? getTimestampAxis(xMax) : getCategoryAxis(),
      },
    };
  }, [direction, valueAxis, xScaleType, xMax, showValueLabels]);

  const chartData = useMemo<ChartData<"bar">>(
    () => ({
      labels,
      datasets: datasets.map((ds) => ({
        label: ds.label,
        // On a linear axis bars are positioned by their numeric x; xValues is
        // co-indexed with each dataset's data by construction. chart.js parses
        // {x, y} points for bars at runtime, but its types only model numbers
        // here, hence the cast.
        data: (xValues
          ? ds.data.map((y, i) => ({ x: xValues[i] ?? 0, y }))
          : ds.data) as unknown as number[],
        backgroundColor: ds.color,
        borderRadius: 4,
        barPercentage,
        categoryPercentage,
      })),
    }),
    [labels, datasets, xValues, barPercentage, categoryPercentage]
  );

  const mergedOptions = useMemo<ChartOptions<"bar">>(() => {
    if (!chartOptions) return defaultOptions;
    return {
      ...defaultOptions,
      ...chartOptions,
      plugins: {
        ...defaultOptions.plugins,
        ...chartOptions.plugins,
      },
      scales: {
        ...defaultOptions.scales,
        ...chartOptions.scales,
      },
    };
  }, [defaultOptions, chartOptions]);

  if (loading) {
    return (
      <AnalyticsCard title={title} subtitle={subtitle} loading>
        <Skeleton variant="rectangular" width="100%" height={plotHeight ?? height} />
      </AnalyticsCard>
    );
  }

  return (
    <AnalyticsCard
      title={title}
      subtitle={subtitle}
      info={info}
      badge={badge}
    >
      <div
        style={
          plotHeight == null
            ? { flex: 1, maxHeight: height }
            : { flex: "1 0 auto", height: plotHeight, position: "relative" }
        }
      >
        <Bar
          data={chartData}
          options={mergedOptions}
          plugins={showValueLabels ? [valueLabelsPlugin] : []}
        />
      </div>
    </AnalyticsCard>
  );
};
